import { FlexColumnCenterVertical } from '@main/core-ui';
import React from 'react';
import { useTheme } from 'styled-components';

import { RadioInput } from '../RadioInput';

interface RadioGroupOption<T extends string> {
  /** message to be translated for the option label */
  label: string;
  /** value passed to onChange */
  value: T;
}

/**
 * type of object passed to RadioGroup options prop
 */
export type RadioGroupOptions<T extends string> = Array<RadioGroupOption<T>>;

export interface RadioGroupProps<
  T extends string,
  K extends Array<RadioGroupOption<T>>,
  J = K[number]['value'],
> extends Omit<
    React.ComponentPropsWithRef<'fieldset'>,
    'children' | 'onChange' | 'type' | 'title'
  > {
  /** current checked value of group */
  value: J | undefined;
  /** title of radio group */
  title?: string;
  /** description of title */
  titleDescription?: string | React.ReactNode;
  /** individual radio group items */
  options: K;
  /** callback for when a radio input is checked */
  onChange?: (value: J | null) => void;
  /** Flex direction of the radio group */
  direction?: 'row' | 'column';
  /** show radio circle */
  showCircle?: boolean;
  /** flag for whether radio input is disabled */
  disabled?: boolean;
  /** name of the radio group */
  name: string;
}

export const RadioGroup: <
  T extends string,
  K extends Array<RadioGroupOption<T>>,
>(
  props: RadioGroupProps<T, K> & {
    /** internal name for ref as forwardRef components don't support generics, so we're doing a workaround */
    fieldsetRef?: React.Ref<HTMLFieldSetElement>;
  },
) => React.ReactElement = ({
  value = null,
  onChange,
  title,
  titleDescription,
  fieldsetRef,
  direction = 'row',
  showCircle,
  options,
  disabled,
  name,
  ...props
}) => {
  const theme = useTheme();
  return (
    <fieldset
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
      ref={fieldsetRef}
      {...props}
    >
      <FlexColumnCenterVertical>
        {title && <h3>{title}</h3>}
        {titleDescription && (
          <p style={{ color: theme.colors.textLightOnBg, marginBottom: 0 }}>
            {titleDescription}
          </p>
        )}
      </FlexColumnCenterVertical>

      <div
        style={{
          display: 'flex',
          flexDirection: direction,
          flexWrap: 'wrap',
          gap: '6px',
        }}
      >
        {options.map(({ value: optionValue, label }) => (
          <RadioInput
            key={optionValue}
            name={name}
            value={optionValue}
            checked={optionValue === value}
            onChange={() => onChange?.(optionValue)}
            showCircle={showCircle}
            disabled={disabled}
          >
            <span>{label}</span>
          </RadioInput>
        ))}
      </div>
    </fieldset>
  );
};
